import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { chatApi } from '@/api/client';
import type { ChatActionProposal } from '@/types/chat';

export function chatActionsQueryKey(profileId: string) {
  return ['chat-actions', profileId] as const;
}

export function chatProposalQueryKey(
  profileId: string,
  threadId: string,
  proposalId: string
) {
  return ['chat-proposal', profileId, threadId, proposalId] as const;
}

export function useChatActionHistory(profileId: string, enabled = true) {
  return useQuery({
    queryKey: chatActionsQueryKey(profileId),
    queryFn: async () => {
      const res = await chatApi.listActions(profileId, { limit: 50 });
      if (!res.success) throw new Error('Failed to load action history');
      return res.data?.items ?? [];
    },
    enabled: Boolean(profileId) && enabled,
  });
}

export function useChatProposal(
  profileId: string,
  threadId: string | null,
  proposalId: string | null
) {
  return useQuery({
    queryKey: chatProposalQueryKey(profileId, threadId ?? '', proposalId ?? ''),
    queryFn: async () => {
      if (!threadId || !proposalId) return null;
      const res = await chatApi.getProposal(profileId, threadId, proposalId);
      if (!res.success || !res.data) throw new Error('Failed to load proposal');
      return res.data as ChatActionProposal;
    },
    enabled: Boolean(profileId && threadId && proposalId),
  });
}

export function useChatAllowlist(profileId: string) {
  return useQuery({
    queryKey: ['chat-allowlist', profileId],
    queryFn: async () => {
      const res = await chatApi.getAllowlist(profileId);
      if (!res.success || !res.data) return { enabled: false, action_types: [] as string[] };
      return res.data;
    },
    enabled: Boolean(profileId),
  });
}

function invalidateActionQueries(
  qc: ReturnType<typeof useQueryClient>,
  profileId: string,
  threadId: string,
  proposalId: string
) {
  void qc.invalidateQueries({ queryKey: chatActionsQueryKey(profileId) });
  void qc.invalidateQueries({
    queryKey: chatProposalQueryKey(profileId, threadId, proposalId),
  });
}

export function useConfirmChatProposal(profileId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      threadId,
      proposalId,
    }: {
      threadId: string;
      proposalId: string;
    }) => {
      const res = await chatApi.confirmProposal(profileId, threadId, proposalId);
      if (!res.success || !res.data) throw new Error('Confirm failed');
      return res.data;
    },
    onSuccess: (data) => {
      invalidateActionQueries(qc, profileId, data.thread_id, data.id);
    },
  });
}

export function useRejectChatProposal(profileId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      threadId,
      proposalId,
    }: {
      threadId: string;
      proposalId: string;
    }) => {
      const res = await chatApi.rejectProposal(profileId, threadId, proposalId);
      if (!res.success || !res.data) throw new Error('Reject failed');
      return res.data;
    },
    onSuccess: (data) => {
      invalidateActionQueries(qc, profileId, data.thread_id, data.id);
    },
  });
}

export function usePatchChatProposal(profileId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      threadId,
      proposalId,
      selected_item_ids,
      params,
    }: {
      threadId: string;
      proposalId: string;
      selected_item_ids?: string[];
      params?: Record<string, unknown>;
    }) => {
      const res = await chatApi.patchProposal(profileId, threadId, proposalId, {
        selected_item_ids,
        params,
      });
      if (!res.success || !res.data) throw new Error('Update failed');
      return res.data;
    },
    onSuccess: (data) => {
      invalidateActionQueries(qc, profileId, data.thread_id, data.id);
    },
  });
}

export function useUndoChatProposal(profileId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      threadId,
      proposalId,
    }: {
      threadId: string;
      proposalId: string;
    }) => {
      const res = await chatApi.undoProposal(profileId, threadId, proposalId);
      if (!res.success || !res.data) throw new Error('Undo failed');
      return res.data;
    },
    onSuccess: (data, vars) => {
      invalidateActionQueries(qc, profileId, vars.threadId, vars.proposalId);
      invalidateActionQueries(qc, profileId, data.thread_id, data.id);
    },
  });
}
